import React, { useState, useEffect } from 'react';
import { User, Mail, Lock, Eye, EyeOff, CheckCircle, XCircle, RefreshCw, Copy, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { csrfFetch } from '../utils/csrfFetch';
import { buildApiUrl } from '../config';

const ProfileSettings = () => {
  const { user } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [lastResendTime, setLastResendTime] = useState(0);
  const [changingPassword, setChangingPassword] = useState(false);

  const [profileData, setProfileData] = useState({
    email: user?.email || '',
    isEmailConfirmed: false,
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [twoFactorSharedKey, setTwoFactorSharedKey] = useState('');
  const [twoFactorRecoveryCodes, setTwoFactorRecoveryCodes] = useState([]);
  const [recoveryCodesLeft, setRecoveryCodesLeft] = useState(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [reauthPassword, setReauthPassword] = useState('');
  const [reauthCode, setReauthCode] = useState('');
  const [reauthVerified, setReauthVerified] = useState(false);
  const [reauthLoading, setReauthLoading] = useState(false);
  const [setupLoading, setSetupLoading] = useState(false);
  const [enableLoading, setEnableLoading] = useState(false);
  const [disableLoading, setDisableLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [showTwoFADialog, setShowTwoFADialog] = useState(false);
  const [twoFaStep, setTwoFaStep] = useState('start'); // start -> reauth -> qr -> verify -> done
  const [qrImageUrl, setQrImageUrl] = useState('');

  const resetTwoFaFlow = () => {
    setReauthPassword('');
    setReauthCode('');
    setTwoFactorCode('');
    setTwoFactorSharedKey('');
    setTwoFactorRecoveryCodes([]);
    setQrImageUrl('');
    setReauthVerified(false);
    setTwoFaStep('start');
  };

  const openTwoFaDialog = () => {
    resetTwoFaFlow();
    setShowTwoFADialog(true);
    setTwoFaStep(twoFactorEnabled ? 'status' : 'start');
  };

  const closeTwoFaDialog = () => {
    setShowTwoFADialog(false);
    resetTwoFaFlow();
  };

  const handleContinueAfterQr = () => setTwoFaStep('verify');

  // Fetch email verification status from API
  const fetchEmailVerificationStatus = async () => {
    try {
      setLoading(true);
      const response = await fetch(buildApiUrl('/account/manage/info'), {
        method: 'GET',
        headers: {
          'accept': '*/*'
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setProfileData(prev => ({
          ...prev,
          email: data.email || prev.email,
          isEmailConfirmed: data.isEmailConfirmed || false
        }));
      } else {
        toast.error('Failed to load email verification status');
      }
    } catch (error) {
      toast.error('Error loading email verification status');
    } finally {
      setLoading(false);
    }
  };

  const fetchTwoFactorStatus = async () => {
    setStatusLoading(true);
    try {
      const response = await csrfFetch('/account/manage/2fa', {
        method: 'GET',
        headers: { accept: 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        if (typeof data.isTwoFactorEnabled !== 'undefined') {
          setTwoFactorEnabled(!!data.isTwoFactorEnabled);
        }
        if (typeof data.recoveryCodesLeft === 'number') {
          setRecoveryCodesLeft(data.recoveryCodesLeft);
        }
        if (Array.isArray(data.recoveryCodes)) {
          setTwoFactorRecoveryCodes(data.recoveryCodes);
        }
      }
    } catch (error) {
      console.error('Failed to fetch 2FA status:', error);
    } finally {
      setStatusLoading(false);
    }
  };

  // Fetch email verification status on component mount
  useEffect(() => {
    fetchEmailVerificationStatus();
    fetchTwoFactorStatus();
  }, []);

  const handleResendVerificationEmail = async () => {
    const now = Date.now();
    const cooldownPeriod = 30000;
    const timeSinceLastResend = now - lastResendTime;
    
    if (timeSinceLastResend < cooldownPeriod) {
      const remainingTime = Math.ceil((cooldownPeriod - timeSinceLastResend) / 1000);
      toast.error(`Please wait ${remainingTime} seconds before requesting another email.`);
      return;
    }

    try {
      setResendingEmail(true);
      const response = await csrfFetch('/account/resendConfirmationEmail', {
        method: 'POST',
        headers: {
          'accept': '*/*',
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ email: profileData.email })
      });

      if (response.ok) {
        setLastResendTime(now);
        toast.success('Verification email sent successfully!');
        // Optionally refresh the email verification status after a delay
        setTimeout(() => {
          fetchEmailVerificationStatus();
        }, 2000);
      } else {
        const errorText = await response.text();
        
        // Handle different error status codes
        switch (response.status) {
          case 400:
            toast.error('Invalid request. Please try again.');
            break;
          case 404:
            toast.error('Email address not found.');
            break;
          case 429:
            toast.error('Too many requests. Please wait before trying again.');
            break;
          default:
            toast.error('Failed to send verification email. Please try again.');
        }
      }
    } catch (error) {

      toast.error('Network error. Please check your connection and try again.');
    } finally {
      setResendingEmail(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordData.currentPassword || !passwordData.newPassword) {
      toast.error('Enter current and new password');
      return;
    }

    setChangingPassword(true);
    try {
      const response = await csrfFetch('/account/manage/info', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          newEmail: null,
          newPassword: passwordData.newPassword,
          oldPassword: passwordData.currentPassword,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        // Common backend behavior: wrong old password returns 400/401
        if (response.status === 400 || response.status === 401) {
          throw new Error(errorText || 'Current password is incorrect');
        }
        throw new Error(errorText || `Failed to change password (status ${response.status})`);
      }

      toast.success('Password changed successfully');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (error) {
 
      toast.error(error.message || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const confirmIdentity = async () => {
    if (!reauthPassword && !reauthCode) {
      toast.error('Enter your password or current 2FA code to continue');
      return;
    }

    setReauthLoading(true);
    try {
      const response = await csrfFetch('/account/confirmIdentity', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          password: reauthPassword || null,
          twoFactorCode: reauthCode || null
        })
      });


      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to verify identity');
      }

      setReauthVerified(true);
      toast.success('Identity verified for sensitive actions');
      if (!twoFactorEnabled) {
        setTwoFaStep('qr');
        try {
          await startTwoFactorSetup(true);
        } catch {
          setTwoFaStep('reauth');
        }
      } else {
        setTwoFaStep('status');
      }
    } catch (error) {

      toast.error(('uhuy', error.message) || 'Failed to verify identity');
      setReauthVerified(false);
    } finally {
      setReauthLoading(false);
    }
  };

  const startTwoFactorSetup = async (force = false) => {
    if (!force && !reauthVerified) {
      toast.error('Please verify your identity first');
      return;
    }

    setSetupLoading(true);
    try {
      const response = await csrfFetch('/account/manage/2fa', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          resetSharedKey: true,
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to start 2FA setup');
      }

      const data = await response.json();
      const sharedKey = data.sharedKey || '';
      setTwoFactorSharedKey(sharedKey);
      setTwoFactorRecoveryCodes(data.recoveryCodes || []);
      if (typeof data.recoveryCodesLeft === 'number') {
        setRecoveryCodesLeft(data.recoveryCodesLeft);
      }
      if (typeof data.isTwoFactorEnabled !== 'undefined') {
        setTwoFactorEnabled(!!data.isTwoFactorEnabled);
      }

      if (data.qrCodeUri) {
        setQrImageUrl(data.qrCodeUri);
      } else if (sharedKey) {
        const issuer = encodeURIComponent('Arbitro');
        const label = encodeURIComponent(`Arbitro:${profileData.email || 'user'}`);
        const otpauthUrl = `otpauth://totp/${label}?secret=${sharedKey}&issuer=${issuer}`;
        setQrImageUrl(`https://quickchart.io/qr?text=${encodeURIComponent(otpauthUrl)}&size=220`);
      } else {
        toast.error('No QR or shared key returned from server');
        setTwoFaStep('reauth');
      }

      toast.success('2FA setup key generated');
      fetchTwoFactorStatus();
    } catch (error) {

      toast.error(error.message || 'Failed to start 2FA setup');
    } finally {
      setSetupLoading(false);
    }
  };

  const handleEnableTwoFactor = async () => {
    if (!reauthVerified) {
      toast.error('Please verify your identity first');
      return;
    }
    if (!twoFactorCode || twoFactorCode.trim().length < 6) {
      toast.error('Enter the 6-digit code from your authenticator app');
      return;
    }

    setEnableLoading(true);
    try {
      const response = await csrfFetch('/account/manage/2fa', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          enable: true,
          twoFactorCode: twoFactorCode.trim(),
          resetRecoveryCodes: true,
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to enable 2FA');
      }

      const data = await response.json();
      setTwoFactorEnabled(
        typeof data.isTwoFactorEnabled !== 'undefined' ? !!data.isTwoFactorEnabled : true
      );
      setTwoFactorSharedKey(data.sharedKey || twoFactorSharedKey);
      setTwoFactorRecoveryCodes(data.recoveryCodes || twoFactorRecoveryCodes);
      if (typeof data.recoveryCodesLeft === 'number') {
        setRecoveryCodesLeft(data.recoveryCodesLeft);
      }
      setTwoFactorCode('');
      toast.success('Two-factor authentication enabled');
      setTwoFaStep('done');
      fetchTwoFactorStatus();
    } catch (error) {

      toast.error(error.message || 'Failed to enable 2FA');
    } finally {
      setEnableLoading(false);
    }
  };

  const handleDisableTwoFactor = async () => {
    if (!reauthVerified) {
      toast.error('Please verify your identity first');
      return;
    }

    setDisableLoading(true);
    try {
      const response = await csrfFetch('/account/manage/2fa', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          enable: false,
          twoFactorCode: '',
          resetSharedKey: false,
          resetRecoveryCodes: true,
          forgetMachine: true
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to disable 2FA');
      }

      const data = await response.json();
      setTwoFactorEnabled(false);
      setTwoFactorSharedKey('');
      setTwoFactorRecoveryCodes([]);
      if (typeof data.recoveryCodesLeft === 'number') {
        setRecoveryCodesLeft(data.recoveryCodesLeft);
      }
      toast.success('Two-factor authentication disabled');
      fetchTwoFactorStatus();
    } catch (error) {
  
      toast.error(error.message || 'Failed to disable 2FA');
    } finally {
      setDisableLoading(false);
    }
  };

  const copyToClipboard = async (value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      toast.success('Copied to clipboard');
    } catch (error) {
      toast.error('Failed to copy');
    }
  };


  return (
    <>
    <div className="space-y-6 max-w-4xl">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Profile Settings</h1>
        <p className="text-gray-400">Manage your account settings and preferences</p>
      </div>

      {/* Profile & Security Content */}
      <div className="space-y-6">
        {/* Profile Section */}
        <div className="glass-card p-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              <span className="ml-2 text-gray-400">Loading profile...</span>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex items-center space-x-4 pb-6 border-b border-dark-border">
                <div className="h-20 w-20 bg-blue-600 rounded-full flex items-center justify-center">
                  <User className="h-10 w-10 text-white" />
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <h3 className="text-lg font-semibold text-white">{profileData.email}</h3>
                    {profileData.isEmailConfirmed ? (
                      <CheckCircle className="h-5 w-5 text-green-500" title="Email verified" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-500" title="Email not verified" />
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    {user?.role === 'admin' ? 'Administrator' : 'Trader'}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Email {profileData.isEmailConfirmed ? 'verified' : 'not verified'}
                  </p>
                </div>
              </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="email"
                    value={profileData.email}
                    onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                    className="input-field pl-10 pr-10"
                    disabled
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {profileData.isEmailConfirmed ? (
                      <CheckCircle className="h-5 w-5 text-green-500" title="Email verified" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-500" title="Email not verified" />
                    )}
                  </div>
                </div>
                <p className="mt-1 text-xs text-gray-500">Contact admin to change email</p>
                {!profileData.isEmailConfirmed && (
                  <p className="mt-1 text-xs text-red-400">Email verification required</p>
                )}
              </div>

              {!profileData.isEmailConfirmed && (
                <button 
                  onClick={handleResendVerificationEmail}
                  disabled={resendingEmail}
                  className="btn-secondary text-sm flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {resendingEmail ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Resend Verification Email'
                  )}
                </button>
              )}
            </div>
            </div>
          )}
        </div>

        {/* Security Section */}
        <div className="glass-card p-6">
          <div className="space-y-6">
            <div className="pb-6 border-b border-dark-border">
              <h3 className="text-lg font-semibold text-white mb-4">Change Password</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Current Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                      className="input-field pl-10 pr-10"
                      placeholder="Enter your current password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    New Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                      className="input-field pl-10 pr-10"
                      placeholder="Enter your new password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300"
                    >
                      {showNewPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                      className="input-field pl-10"
                      placeholder="Confirm your new password"
                    />
                  </div>
                </div>

                <button
                  onClick={handleChangePassword}
                  disabled={changingPassword}
                  className="btn-primary disabled:opacity-60"
                >
                  {changingPassword ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </div>

            <div className="glass-card p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-white flex items-center space-x-2">
                    <span>Two-Factor Authentication</span>
                    <span className={`px-2 py-1 text-xs rounded-md ${twoFactorEnabled ? 'bg-green-600/30 text-green-300' : 'bg-gray-700 text-gray-200'}`}>
                      {twoFactorEnabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </h3>
                  <p className="text-sm text-gray-400">Protect logins with an authenticator app, similar to major crypto exchanges.</p>
                </div>
                <button
                  onClick={openTwoFaDialog}
                  className="btn-primary text-sm"
                >
                  Manage 2FA
                </button>
              </div>
              <div className="text-xs text-gray-500">
                Status updates automatically. Last checked: {statusLoading ? 'loading...' : new Date().toLocaleTimeString()}
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>

    {showTwoFADialog && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
        <div className="bg-gray-900 border border-gray-800 rounded-xl shadow-2xl max-w-3xl w-full">
           {/*Header*/}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
            <div>
              <p className="text-sm uppercase tracking-wide text-blue-400">Security</p>
              <h3 className="text-xl font-semibold text-white">Two-Factor Authentication</h3>
            </div>
            <button
              onClick={closeTwoFaDialog}
              className="text-gray-400 hover:text-white"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/*Current 2FA Status*/}
          <div className="p-6 space-y-5">
            <div className="flex items-center justify-between bg-gray-800/60 border border-gray-800 rounded-lg px-4 py-3">
              <div>
                <p className="text-white font-medium">Current status</p>
                <p className="text-sm text-gray-400">2FA is {twoFactorEnabled ? 'enabled' : 'disabled'} for your account.</p>
              </div>
              <span className={`px-2 py-1 text-xs rounded-md ${twoFactorEnabled ? 'bg-green-600/30 text-green-300' : 'bg-gray-700 text-gray-200'}`}>
                {twoFactorEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>

            {/*Step by Step Capsule*/}
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className={`px-3 py-1 rounded-full ${twoFaStep === 'start' ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-300'}`}>1. Start</span>
              <span className={`px-3 py-1 rounded-full ${twoFaStep === 'reauth' ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-300'}`}>2. Verify identity</span>
              <span className={`px-3 py-1 rounded-full ${twoFaStep === 'qr' ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-300'}`}>3. Scan QR</span>
              <span className={`px-3 py-1 rounded-full ${twoFaStep === 'verify' ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-300'}`}>4. Enter code</span>
              <span className={`px-3 py-1 rounded-full ${twoFaStep === 'done' ? 'bg-green-600 text-white' : 'bg-gray-800 text-gray-300'}`}>5. Done</span>
            </div>

            {/*Step 1: Start 2FA*/}
            {!twoFactorEnabled && twoFaStep === 'start' && (
              <div className="bg-gray-800/60 border border-gray-800 rounded-lg p-4 flex items-center justify-between">
                <div>
                  <p className="text-white font-medium">Begin 2FA setup</p>
                  <p className="text-sm text-gray-400">We’ll guide you through password verification, QR scan, and OTP.</p>
                </div>
                <button
                  onClick={() => setTwoFaStep('reauth')}
                  className="btn-primary text-sm"
                >
                  Start setup
                </button>
              </div>
            )}

            {/*Step 2: Re-authenticate*/}
            {twoFaStep === 'reauth' && (
              <div className="bg-gray-800/60 border border-gray-800 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-medium">Verify identity</p>
                    <p className="text-xs text-gray-400">
                      {twoFactorEnabled ? 'Required before disabling 2FA.' : 'Required before generating QR.'}
                    </p>
                  </div>
                  {reauthVerified && (
                    <span className="text-xs text-green-400">Verified</span>
                  )}
                </div>
                <div className="space-y-2">
                  <label className="block text-sm text-gray-400">Password</label>
                  <input
                    type="password"
                    value={reauthPassword}
                    onChange={(e) => setReauthPassword(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter your password"
                  />
                  {twoFactorEnabled && (
                    <>
                      <label className="block text-sm text-gray-400">Existing 2FA code (optional)</label>
                      <input
                        type="text"
                        value={reauthCode}
                        onChange={(e) => setReauthCode(e.target.value)}
                        className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="123456"
                      />
                    </>
                  )}
                  <button
                    onClick={confirmIdentity}
                    disabled={reauthLoading}
                    className="btn-secondary text-sm flex items-center justify-center w-full"
                  >
                    {reauthLoading ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      'Verify identity'
                    )}
                  </button>
                </div>
              </div>
            )}

            {/*Step 3: Show QR Code*/}
            {twoFaStep === 'qr' && !twoFactorEnabled && (
              <div className="bg-gray-800/60 border border-gray-800 rounded-lg p-4 space-y-3">
                <p className="text-white font-medium">Scan this QR with your authenticator</p>
                {qrImageUrl ? (
                  <div className="flex items-center justify-center">
                    <img src={qrImageUrl} alt="2FA QR" className="h-44 w-44 border border-gray-800 rounded-lg bg-white p-2" />
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">QR unavailable. Use the shared key below.</p>
                )}
                {twoFactorSharedKey && (
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Shared key (enter into authenticator if QR fails)</p>
                    <div className="flex items-center justify-between bg-gray-900 border border-gray-700 rounded-md px-3 py-2">
                      <span className="font-mono text-white break-all">{twoFactorSharedKey}</span>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(twoFactorSharedKey)}
                        className="text-gray-300 hover:text-white"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
                <button
                  onClick={handleContinueAfterQr}
                  className="btn-primary text-sm w-full"
                >
                  I’ve scanned the QR, continue
                </button>
              </div>
            )}

            {/*Step 4: Enter Authentication 6-code*/}
            {twoFaStep === 'verify' && !twoFactorEnabled && (
              <div className="bg-gray-800/60 border border-gray-800 rounded-lg p-4 space-y-3">
                <p className="text-white font-medium">Enter the 6-digit code from your authenticator</p>
                <input
                  type="text"
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="123456"
                />
                <button
                  onClick={handleEnableTwoFactor}
                  disabled={enableLoading}
                  className="btn-primary w-full flex items-center justify-center"
                >
                  {enableLoading ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Enabling...
                    </>
                  ) : (
                    'Enable 2FA'
                  )}
                </button>
              </div>
            )}

            {/*Step 5: Show Successful Setup*/}
            {twoFaStep === 'done' && (
              <div className="bg-gray-800/60 border border-gray-800 rounded-lg p-4 space-y-3">
                <p className="text-white font-medium">2FA enabled</p>
                <p className="text-xs text-gray-400">Save your recovery codes. Each can be used once.</p>
                {twoFactorRecoveryCodes && twoFactorRecoveryCodes.length > 0 ? (
                  <div className="bg-black border border-gray-800 rounded-md p-3 space-y-1">
                    {twoFactorRecoveryCodes.map((code) => (
                      <div key={code} className="font-mono text-sm text-gray-200">{code}</div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No recovery codes available.</p>
                )}
              </div>
            )}

            {/*Show Button to Disable 2FA (hide during fresh enable flow)*/}
            {twoFactorEnabled && twoFaStep !== 'done' && (
              <div className="bg-gray-800/60 border border-gray-800 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-medium">Disable 2FA</p>
                    <p className="text-xs text-gray-400">Requires recent identity verification.</p>
                    {!reauthVerified && (
                      <button
                        onClick={() => {
                          resetTwoFaFlow();
                          setTwoFaStep('reauth');
                        }}
                        className="mt-2 text-xs text-blue-400 hover:text-blue-300 underline"
                      >
                        Verify identity to disable
                      </button>
                    )}
                  </div>
                  <button
                    onClick={handleDisableTwoFactor}
                    disabled={disableLoading}
                    className="btn-danger text-sm flex items-center"
                  >
                    {disableLoading ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Disabling...
                      </>
                    ) : (
                      'Disable 2FA'
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default ProfileSettings;
